import { CHUNK_SIZE, COLUMN_HEIGHT, WORLD_EXTENT, WORLD_HEIGHT } from "./constants";
import { PaletteSubChunk } from "./palette";
import { DEFAULT_BLOCKS } from "./registry";
import { AIR, type BlockEdit, type BlockId, type BlockRegistry, type ChunkPos, type PackedLight, type World } from "./types";

export type WorldChangeListener = (dirty: readonly ChunkPos[], edit: BlockEdit) => void;

export function chunkKey(cx: number, cy: number, cz: number): string {
  return `${cx},${cy},${cz}`;
}

export function worldToChunk(value: number): number {
  return Math.floor(value / CHUNK_SIZE);
}

export function worldToLocal(value: number): number {
  const result = value % CHUNK_SIZE;
  return result < 0 ? result + CHUNK_SIZE : result;
}

/** Headless implementation of the locked World interface. */
export class VoxelWorld implements World {
  readonly seed: number;
  private currentTick = 0;
  readonly registry: BlockRegistry;
  readonly edits: BlockEdit[] = [];
  private readonly chunks = new Map<string, PaletteSubChunk>();
  private readonly listeners = new Set<WorldChangeListener>();

  constructor(seed: number, registry: BlockRegistry = DEFAULT_BLOCKS) {
    this.seed = seed;
    this.registry = registry;
  }

  get tick(): number {
    return this.currentTick;
  }

  /** Number of generated sub-chunks currently resident in memory. */
  get loadedChunkCount(): number {
    return this.chunks.size;
  }

  setTick(tick: number): void {
    if (!Number.isSafeInteger(tick) || tick < this.currentTick) {
      throw new RangeError("world ticks must be safe integers and monotonic");
    }
    this.currentTick = tick;
  }

  loadChunk(position: ChunkPos, chunk = new PaletteSubChunk()): boolean {
    if (!this.validChunk(position.cx, position.cy, position.cz)) return false;
    this.chunks.set(chunkKey(position.cx, position.cy, position.cz), chunk);
    return true;
  }

  unloadChunk(position: ChunkPos): PaletteSubChunk | undefined {
    const key = chunkKey(position.cx, position.cy, position.cz);
    const chunk = this.chunks.get(key);
    this.chunks.delete(key);
    return chunk;
  }

  getChunk(cx: number, cy: number, cz: number): PaletteSubChunk | undefined {
    return this.chunks.get(chunkKey(cx, cy, cz));
  }

  getBlock(x: number, y: number, z: number): BlockId {
    if (!this.validBlock(x, y, z)) return AIR;
    const chunk = this.getChunk(worldToChunk(x), worldToChunk(y), worldToChunk(z));
    return chunk?.get(worldToLocal(x), worldToLocal(y), worldToLocal(z)) ?? AIR;
  }

  getLight(x: number, y: number, z: number): PackedLight {
    if (!this.validBlock(x, y, z)) return 0;
    const chunk = this.getChunk(worldToChunk(x), worldToChunk(y), worldToChunk(z));
    return chunk?.getLight(worldToLocal(x), worldToLocal(y), worldToLocal(z)) ?? 0;
  }

  isSolid(x: number, y: number, z: number): boolean {
    return this.registry.get(this.getBlock(x, y, z)).solid;
  }

  isLoaded(x: number, y: number, z: number): boolean {
    if (!this.validBlock(x, y, z)) return false;
    return this.chunks.has(chunkKey(worldToChunk(x), worldToChunk(y), worldToChunk(z)));
  }

  setBlock(x: number, y: number, z: number, id: BlockId): BlockId | null {
    if (!this.validBlock(x, y, z) || !Number.isInteger(id) || id < 0 || id > 0xffff) return null;
    const cx = worldToChunk(x);
    const cy = worldToChunk(y);
    const cz = worldToChunk(z);
    const chunk = this.getChunk(cx, cy, cz);
    if (!chunk) return null;

    const lx = worldToLocal(x);
    const ly = worldToLocal(y);
    const lz = worldToLocal(z);
    const previous = chunk.set(lx, ly, lz, id);
    if (previous === id) return previous;

    const edit: BlockEdit = Object.freeze({ x, y, z, id, tick: this.currentTick });
    this.edits.push(edit);
    const dirty: ChunkPos[] = [{ cx, cy, cz }];
    if (lx === 0) dirty.push({ cx: cx - 1, cy, cz });
    if (lx === CHUNK_SIZE - 1) dirty.push({ cx: cx + 1, cy, cz });
    if (ly === 0) dirty.push({ cx, cy: cy - 1, cz });
    if (ly === CHUNK_SIZE - 1) dirty.push({ cx, cy: cy + 1, cz });
    if (lz === 0) dirty.push({ cx, cy, cz: cz - 1 });
    if (lz === CHUNK_SIZE - 1) dirty.push({ cx, cy, cz: cz + 1 });
    const resident = dirty.filter((position) => this.getChunk(position.cx, position.cy, position.cz) !== undefined);
    for (const listener of this.listeners) listener(resident, edit);
    return previous;
  }

  subscribe(listener: WorldChangeListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private validBlock(x: number, y: number, z: number): boolean {
    return Number.isInteger(x) && Number.isInteger(y) && Number.isInteger(z)
      && x >= 0 && x < WORLD_EXTENT && z >= 0 && z < WORLD_EXTENT
      && y >= 0 && y < WORLD_HEIGHT;
  }

  private validChunk(cx: number, cy: number, cz: number): boolean {
    const columnWidth = WORLD_EXTENT / CHUNK_SIZE;
    return Number.isInteger(cx) && Number.isInteger(cy) && Number.isInteger(cz)
      && cx >= 0 && cx < columnWidth && cz >= 0 && cz < columnWidth
      && cy >= 0 && cy < COLUMN_HEIGHT;
  }
}
